import React, { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQRDelivery } from "@/hooks/useQRDelivery";
// import { Button } from "@/components/ui/button";
// import { DeliveryProcessingInterface } from "./DeliveryProcessingInterface";
// import { DeliverySessionSummary } from "./DeliverySessionSummary";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle, User, Package, AlertCircle, QrCode, Clock } from "lucide-react";
import { useToast } from "@/hooks/useToast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Estados del flujo de entrega
enum QRDeliveryModalState {
  SCANNING = "scanning",
  VALIDATING = "validating",
  PROCESSING = "processing",
  COMPLETED = "completed",
  ERROR = "error",
}

interface QRDeliveryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const QRDeliveryModal: React.FC<QRDeliveryModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { user } = useAuth();
  const { toast } = useToast();

  const {
    state,
    resetState,
    isLoading,
    error,
  } = useQRDelivery();

  // Determinar el estado actual basado en los datos del hook
  const getCurrentState = (): QRDeliveryModalState => {
    if (error) return QRDeliveryModalState.ERROR;
    if (state.deliverySession?.status === "completed")
      return QRDeliveryModalState.COMPLETED;
    if (state.deliverySession?.status === "in_progress")
      return QRDeliveryModalState.PROCESSING;
    if (state.qrValidation) return QRDeliveryModalState.VALIDATING;
    return QRDeliveryModalState.SCANNING;
  };

  const currentState = getCurrentState();

  // Verificar que el usuario tenga permisos de cajero
  useEffect(() => {
    if (user && !["Cajero", "Admin"].includes(user.roles?.[0] || "")) {
      toast({
        title: "Acceso denegado",
        description: "No tienes permisos para acceder al modo cajero.",
        variant: "destructive",
      });
      onClose();
    }
  }, [user, onClose, toast]);

  // Manejar cancelación
  const handleCancel = () => {
    resetState();
    onClose();
  };

  // Renderizar componente según el estado actual
  const renderCurrentComponent = () => {
    switch (currentState) {
      case QRDeliveryModalState.SCANNING:
        return (
          <div className="p-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Componente QRDeliveryScanner no implementado
              </AlertDescription>
            </Alert>
          </div>
        );

      case QRDeliveryModalState.VALIDATING:
        return (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  QR Validado - Productos del Estudiante
                </CardTitle>
              </CardHeader>
              <CardContent>
                {state.qrValidation && (
                  <div className="space-y-4">
                    {/* Información del estudiante */}
                    <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-2 mb-2">
                        <User className="h-4 w-4 text-blue-600" />
                        <span className="font-medium text-blue-800 dark:text-blue-200">
                          Estudiante: {state.qrValidation.parentName}
                        </span>
                      </div>
                      <div className="text-sm text-blue-700 dark:text-blue-300">
                        Productos seleccionados: {state.qrValidation.productName}
                      </div>
                    </div>

                    {/* Lista de productos disponibles para entrega */}
                    <div className="space-y-3">
                      <h4 className="font-medium flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Productos a Entregar
                      </h4>
                      {state.qrValidation.availableDeliveries.map((delivery: any) => (
                        <div
                          key={delivery.assignmentId}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div>
                            <p className="font-medium">{delivery.productName}</p>
                            <p className="text-sm text-muted-foreground">
                              Cantidad: {delivery.remainingQuantity}
                            </p>

                            {delivery.delivered && (
                              <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                                ✅ Entregado
                              </span>
                            )}
                            
                          </div>
                          {!delivery.delivered && (
                            <button
                              onClick={() => {
                                toast({
                                  title: "Funcionalidad no implementada",
                                  description: "La entrega de productos aún no está implementada",
                                  variant: "destructive",
                                });
                              }}
                              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                              disabled={isLoading}
                            >
                              {isLoading ? "Procesando..." : "Confirmar Entrega"}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );

      case QRDeliveryModalState.PROCESSING:
        return (
          <div className="p-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Componente DeliveryProcessingInterface no implementado
              </AlertDescription>
            </Alert>
          </div>
        );

      case QRDeliveryModalState.COMPLETED:
        return (
          <div className="p-4">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Componente DeliverySessionSummary no implementado
              </AlertDescription>
            </Alert>
          </div>
        );

      case QRDeliveryModalState.ERROR:
        return (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {error || "Error desconocido"}
            </AlertDescription>
          </Alert>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="h-5 w-5" />
            Sistema de Entrega QR - Productos de Estudiantes
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Indicador de estado */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              Estado:{" "}
              {currentState === QRDeliveryModalState.SCANNING && "Escaneando QR"}
              {currentState === QRDeliveryModalState.VALIDATING && "Validando Productos"}
              {currentState === QRDeliveryModalState.PROCESSING && "Procesando Entrega"}
              {currentState === QRDeliveryModalState.COMPLETED && "Entrega Completada"}
              {currentState === QRDeliveryModalState.ERROR && "Error"}
            </span>
          </div>

          {/* Contenido principal */}
          {renderCurrentComponent()}

          {/* Botones de acción */}
          {currentState !== QRDeliveryModalState.COMPLETED && currentState !== QRDeliveryModalState.ERROR && (
            <div className="flex justify-end gap-2 pt-4 border-t">
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors"
              >
                Cancelar
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRDeliveryModal;
